import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import BrianyCareLogo from "./logo.png";
import { reportDaySeconds } from "./reportHours";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

/* ─────────────── PAGINATION ─────────────── */
// First page has a tall header (~110pt); other pages have a short title (~20pt).
// Footer is 30pt tall. We reserve that space via paddingBottom on the page.
// Row height ≈ 24pt each.
const FIRST_PAGE_ROWS = 14;
const OTHER_PAGE_ROWS = 17;

const chunkPages = (arr) => {
  const pages = [];
  if (!arr.length) return pages;
  pages.push(arr.slice(0, FIRST_PAGE_ROWS));
  let i = FIRST_PAGE_ROWS;
  while (i < arr.length) {
    pages.push(arr.slice(i, i + OTHER_PAGE_ROWS));
    i += OTHER_PAGE_ROWS;
  }
  return pages;
};

/* ─────────────── HELPERS ─────────────── */
const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const formatDate = (d) => (d ? dayjs(d).format("MMM DD, YYYY") : "N/A");

const secondsToHM = (sec) => {
  const total = Number(sec || 0);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  return `${h}:${String(m).padStart(2, "0")}`;
};

const buildSummaryRows = (employees, startDate, endDate) => {
  const start = startDate ? dayjs(startDate) : null;
  const end   = endDate   ? dayjs(endDate)   : null;

  return employees
    .map((emp) => {
      const daySec = { Mon:0, Tue:0, Wed:0, Thu:0, Fri:0, Sat:0, Sun:0 };

      emp.recap?.forEach((r) => {
        const d = dayjs(r.date);
        const inRange =
          (!start || d.isSameOrAfter(start, "day")) &&
          (!end   || d.isSameOrBefore(end,   "day"));

        if (inRange) {
          const label = d.format("ddd");
          if (daySec[label] !== undefined) {
            // owner 10-19: approved hours (v2 recap approved_hours: automatic clock-outs count once their
            // correction is approved, Deleted rows never); a recap without them: Submitted days only, as before
            daySec[label] += reportDaySeconds(r);
          }
        }
      });

      const totalSec = Object.values(daySec).reduce((a, b) => a + b, 0);
      if (totalSec === 0) return null;
      return { emp, daySec, totalSec };
    })
    .filter(Boolean);
};

/* ─────────────── STYLES ─────────────── */
const S = StyleSheet.create({
  // paddingBottom=50 reserves space so the fixed footer never overlaps content
  page: { paddingTop: 30, paddingHorizontal: 30, paddingBottom: 50, fontSize: 9, fontFamily: "Helvetica" },

  headerContainer: {
    alignItems: "center",
    marginBottom: 18,
    borderBottom: "1.5 solid #1e3a5f",
    paddingBottom: 12,
  },
  logo:        { width: 180, height: 35, marginBottom: 6 },
  mainHeading: { fontSize: 14, fontWeight: "bold", color: "#1e3a5f", marginTop: 4 },
  subHeading:  { fontSize: 10, color: "#475569", marginTop: 3 },
  badgeRow:    { flexDirection: "row", gap: 8, marginTop: 7 },
  badge:       { paddingHorizontal: 10, paddingVertical: 3, backgroundColor: "#dbeafe", borderRadius: 10 },
  badgeText:   { fontSize: 8, color: "#1e40af", fontWeight: "bold" },

  table:       { width: "100%", border: "1 solid #cbd5e1" },
  tableHeader: { flexDirection: "row", backgroundColor: "#1e3a5f" },

  // wrap={false} is applied inline on each row View to prevent mid-row splits
  tableRow:    { flexDirection: "row", borderTop: "1 solid #e2e8f0" },
  tableRowAlt: { flexDirection: "row", borderTop: "1 solid #e2e8f0", backgroundColor: "#f0f7ff" },
  totalRow:    { flexDirection: "row", borderTop: "2 solid #1e3a5f", backgroundColor: "#dbeafe" },

  colNo:    { width: "4%",  padding: 5, borderRight: "1 solid #e2e8f0" },
  colName:  { width: "22%", padding: 5, borderRight: "1 solid #e2e8f0" },
  colDay:   { width: "8%",  padding: 4, borderRight: "1 solid #e2e8f0", textAlign: "center" },
  colTotal: { width: "10%", padding: 5, textAlign: "center" },

  headerText: { color: "#fff", fontSize: 8, fontWeight: "bold", textAlign: "center" },
  headerLeft: { color: "#fff", fontSize: 8, fontWeight: "bold" },
  cellBold:   { fontSize: 8, fontWeight: "bold" },
  cellCenter: { fontSize: 8, textAlign: "center" },

  nameContainer: { flexDirection: "row", alignItems: "center" },
  empIdBadge:    { marginLeft: 5, paddingHorizontal: 5, paddingVertical: 2, backgroundColor: "#e2e8f0", borderRadius: 8 },
  empIdText:     { fontSize: 7, color: "#475569" },

  // fixed footer sits inside the paddingBottom zone — never touches content
  footer: {
    position: "absolute", bottom: 16, left: 30, right: 30,
    flexDirection: "row", justifyContent: "space-between",
    borderTop: "1 solid #cbd5e1", paddingTop: 5,
  },
  footerText: { fontSize: 7, color: "#94a3b8" },

  pageTitle: { fontSize: 10, fontWeight: "bold", color: "#1e3a5f", marginBottom: 6 },
});

/* ─────────────── COMPONENT ─────────────── */
const EmployeeSummaryReportPDF = ({ employees = [], dateRange }) => {
  const rows  = buildSummaryRows(employees, dateRange?.startDate, dateRange?.endDate);
  const pages = chunkPages(rows);

  const grandDaySec = { Mon:0, Tue:0, Wed:0, Thu:0, Fri:0, Sat:0, Sun:0 };
  rows.forEach((r) => WEEK_DAYS.forEach((d) => { grandDaySec[d] += r.daySec[d]; }));
  const grandTotalSec = Object.values(grandDaySec).reduce((a, b) => a + b, 0);

  const generatedOn = dayjs().format("MMM DD, YYYY hh:mm A");

  return (
    <Document>
      {pages.map((pageRows, pageIndex) => {
        // row index offset for alternating colours and numbering
        const offset = pageIndex === 0
          ? 0
          : FIRST_PAGE_ROWS + (pageIndex - 1) * OTHER_PAGE_ROWS;

        return (
          <Page key={pageIndex} size="A4" orientation="landscape" style={S.page}>

            {/* ── HEADER (first page only) ── */}
            {pageIndex === 0 && (
              <View style={S.headerContainer}>
                <Image src={BrianyCareLogo} style={S.logo} />
                <Text style={S.mainHeading}>EMPLOYEE SUMMARY REPORT</Text>
                <Text style={S.subHeading}>
                  {formatDate(dateRange?.startDate)} — {formatDate(dateRange?.endDate)}
                </Text>
                {/* <View style={S.badgeRow}>
                  <View style={S.badge}><Text style={S.badgeText}>{rows.length} Employees</Text></View>
                  <View style={S.badge}><Text style={S.badgeText}>Total Hours: {secondsToHM(grandTotalSec)}</Text></View>
                  <View style={S.badge}><Text style={S.badgeText}>Generated: {generatedOn}</Text></View>
                </View> */}
              </View>
            )}

            {/* ── CONTINUATION TITLE ── */}
            {pageIndex > 0 && (
              <Text style={S.pageTitle}>
                Employee Summary Report (continued) — Page {pageIndex + 1}
              </Text>
            )}

            {/* ── TABLE ── */}
            <View style={S.table}>

              {/* Column headers */}
              <View style={S.tableHeader} wrap={false}>
                <View style={S.colNo}><Text style={S.headerText}>#</Text></View>
                <View style={S.colName}><Text style={S.headerLeft}>Employee</Text></View>
                {WEEK_DAYS.map((d) => (
                  <View key={d} style={S.colDay}>
                    <Text style={S.headerText}>{d}</Text>
                  </View>
                ))}
                <View style={S.colTotal}><Text style={S.headerText}>Total</Text></View>
              </View>

              {/* Data rows — wrap={false} prevents a row from being split mid-height */}
              {pageRows.map((r, idx) => {
                const globalIdx = offset + idx;
                const rowStyle  = globalIdx % 2 === 0 ? S.tableRow : S.tableRowAlt;
                return (
                  <View key={r.emp.id} style={rowStyle} wrap={false}>
                    <View style={S.colNo}>
                      <Text style={S.cellCenter}>{globalIdx + 1}</Text>
                    </View>
                    <View style={S.colName}>
                      <View style={S.nameContainer}>
                        <Text style={S.cellBold}>{r.emp.name}</Text>
                        <View style={S.empIdBadge}>
                          <Text style={S.empIdText}>{r.emp.id}</Text>
                        </View>
                      </View>
                    </View>
                    {WEEK_DAYS.map((d) => (
                      <View key={d} style={S.colDay}>
                        <Text style={S.cellCenter}>{secondsToHM(r.daySec[d])}</Text>
                      </View>
                    ))}
                    <View style={S.colTotal}>
                      <Text style={{ ...S.cellCenter, fontWeight: "bold" }}>
                        {secondsToHM(r.totalSec)}
                      </Text>
                    </View>
                  </View>
                );
              })}

              {/* Grand total row — last page only */}
              {pageIndex === pages.length - 1 && (
                <View style={S.totalRow} wrap={false}>
                  <View style={S.colNo}><Text style={S.cellBold} /></View>
                  <View style={S.colName}>
                    <Text style={S.cellBold}>TOTAL ({rows.length} emp)</Text>
                  </View>
                  {WEEK_DAYS.map((d) => (
                    <View key={d} style={S.colDay}>
                      <Text style={{ ...S.cellCenter, fontWeight: "bold" }}>
                        {secondsToHM(grandDaySec[d])}
                      </Text>
                    </View>
                  ))}
                  <View style={S.colTotal}>
                    <Text style={{ ...S.cellCenter, fontWeight: "bold" }}>
                      {secondsToHM(grandTotalSec)}
                    </Text>
                  </View>
                </View>
              )}

            </View>

            {/* ── FIXED FOOTER ── */}
            <View style={S.footer} fixed>
              <Text style={S.footerText}>
                Brianys Care · Confidential · Generated {generatedOn}
              </Text>
              <Text
                style={S.footerText}
                render={({ pageNumber, totalPages }) =>
                  `Page ${pageNumber} of ${totalPages}`
                }
              />
            </View>

          </Page>
        );
      })}
    </Document>
  );
};

export default EmployeeSummaryReportPDF;
